import { Session } from '@graph/schemas'
import { useGetResourcesQuery } from '@graph/hooks'
import { H, RequestResponsePair } from 'highlight.run'
import { RequestType } from '@pages/Player/Toolbar/DevToolsWindowV2/utils'
import { getGraphQLResolverName } from '@pages/Player/utils/utils'
import { createContext } from '@util/context/context'
import { useEffect, useState } from 'react'

import { ApolloError } from '@apollo/client'
import { indexedDBFetch } from '@util/db'

export enum LoadingError {
	NetworkResourcesFetchFailed = 'failed to fetch.',
}

interface ResourcesContext {
	resourcesLoading: boolean
	loadingAfter: boolean
	fetchMoreForward: () => Promise<void>
	resources: NetworkResourceWithID[]
	error?: LoadingError | ApolloError
}

interface NetworkResource extends PerformanceResourceTiming {
	startTimeAbs: number
	responseEndAbs: number
	// http specific
	requestResponsePairs?: RequestResponsePair
	displayName?: string
	// websocket specific
	socketId?: string
	type?: 'open' | 'close'
	// Present when this resource was enriched from a trace.
	traceId?: string
	spanId?: string
	traceTimestamp?: string
}

export type NetworkResourceWithID = { id: number } & NetworkResource

const resourceStart = (resource: NetworkResource) => {
	if (resource.startTimeAbs) {
		return resource.startTimeAbs
	}

	return resource.startTime
}

const resourceEnd = (resource: NetworkResource) => {
	if (resource.responseEndAbs) {
		return resource.responseEndAbs
	}

	return resource.responseEnd
}

const buildResources = (resources: NetworkResource[]) => {
	const indexResources = [] as NetworkResourceWithID[]
	const webSocketHash = {} as { [key: string]: NetworkResource }

	resources?.forEach((resource: NetworkResource) => {
		if (
			resource.initiatorType === RequestType.WebSocket &&
			resource.socketId
		) {
			webSocketHash[resource.socketId] = {
				...webSocketHash?.[resource.socketId],
				...resource,
			}

			if (resource.type === 'close') {
				return
			}
		}

		{
			indexResources.push({
				...resource,
				id: indexResources.length,
			})
		}
	})

	return indexResources
		.sort((a, b) => {
			return resourceStart(a) - resourceStart(b)
		})
		.map((resource: NetworkResourceWithID) => {
			const resolverName = getGraphQLResolverName(resource)

			let updatedResource = { ...resource }

			if (resolverName) {
				updatedResource.displayName = `${resolverName} (${resource.name})`
			}

			if (resourceEnd(resource) && resourceStart(resource)) {
				updatedResource = {
					...updatedResource,
					duration: resourceEnd(resource) - resourceStart(resource),
				}
			}

			return updatedResource
		})
}

export const useResources = (
	session: Session | undefined,
): {
	error: LoadingError | ApolloError | undefined
	fetchMoreForward: () => Promise<void>
	loadingAfter: boolean
	resources: NetworkResourceWithID[]
	resourcesLoading: boolean
} => {
	const [error, setError] = useState<LoadingError>()
	const [loading, setLoading] = useState(false)
	const [resources, setResources] = useState<NetworkResourceWithID[]>([])
	const skipQuery = !session?.secure_id || !!session?.resources_url

	const {
		data,
		loading: queryLoading,
		error: dataError,
	} = useGetResourcesQuery({
		variables: {
			session_secure_id: session?.secure_id ?? '',
		},
		fetchPolicy: 'cache-and-network',
		skip: skipQuery,
	})

	useEffect(() => {
		if (!skipQuery) {
			setLoading(queryLoading && data === undefined)
		}
	}, [data, queryLoading, skipQuery])

	useEffect(() => {
		if (!session?.secure_id || !data?.resources) {
			return
		}

		setError(undefined)
		setResources(buildResources(data.resources as NetworkResource[]))
	}, [data?.resources, session?.secure_id])

	useEffect(() => {
		if (!session?.resources_url) {
			return
		}

		setLoading(true)
		;(async () => {
			try {
				let response
				for await (const r of indexedDBFetch(session.resources_url!)) {
					response = r
				}
				if (!response) {
					setResources([])
					return
				}

				const data = await response.json()
				const rawResources = Array.isArray(data)
					? data
					: data?.resources

				setError(undefined)
				setResources(
					buildResources((rawResources ?? []) as NetworkResource[]),
				)
			} catch (e) {
				setError(LoadingError.NetworkResourcesFetchFailed)
				setResources([])
				H.consumeError(
					e instanceof Error ? e : new Error(String(e)),
					'Error direct downloading resources',
				)
			} finally {
				setLoading(false)
			}
		})()
	}, [session?.resources_url])

	return {
		resources,
		resourcesLoading: loading,
		error: error ?? dataError,
		loadingAfter: false,
		fetchMoreForward: async () => {},
	}
}

export const [useResourcesContext, ResourcesContextProvider] =
	createContext<ResourcesContext>('Resources')
